import {
  APP_CAREPROVIDER,
  APP_CLINIC,
  CAREPROVIDER_TAB_TYPE_NEW,
  REDIRECT_PARAM,
} from "core/consts";
import { ENV_PRODUCTION } from "core/model/config";
import { pathToUrl } from "core/model/utils/urls";
import {
  buildAppUrl,
  getAppRoutePrefix,
} from "core/model/utils/urls/getBaseUrl";
import {
  AdmissionsFormType,
  AdmissionsStep,
  AnalyticsSection,
  MessengerTabKinds,
} from "core/types";
import { setUpdatedRequest } from "dsl/ecosystems/AuctionRequest/updatedRequest";
import { AdmissionsInfoScreen } from "dsl/ecosystems/CareproviderAdmissions/InfoScreens";
import { Location, NavigateFunction } from "react-router-dom";
import { generateParams } from "./helpers";

export const getCareproviderHandlerUrls = ({
  location,
  loggedCareproviderId,
}: {
  location: Location;
  loggedCareproviderId?: number;
}) => {
  const { search } = location;

  return {
    changePassword(id?: number) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${
          id ?? loggedCareproviderId
        }/my-password-change`,
        search: `${REDIRECT_PARAM}=${encodeURIComponent(
          `${location.pathname}${location.search}`,
        )}`,
      });
    },
    // Admissions form
    goToAdmissionsForm({
      admissionsId,
      step,
    }: {
      admissionsId: string;
      step: AdmissionsStep;
    }) {
      return pathToUrl({
        pathname: `/careprovider/admissions/${admissionsId}/step/${step}`,
        search: "",
      });
    },
    goToAdmissionsFormPreview({
      formType,
      step,
    }: {
      formType: AdmissionsFormType;
      step: AdmissionsStep;
    }) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/admissions/form/${formType}/step/${step}`,
        search,
      });
    },
    goToAdmissionsInfoScreens({
      admissionsId,
      infoType,
    }: {
      admissionsId: string;
      infoType: AdmissionsInfoScreen;
    }) {
      return pathToUrl({
        pathname: `/careprovider/admissions/${admissionsId}/info/${infoType}`,
        search: "",
      });
    },
    goToAdmissionsLogin({ admissionsId }: { admissionsId: string }) {
      return pathToUrl({
        pathname: `/careprovider/admissions/${admissionsId}/login`,
        search: "",
      });
    },
    goToAuctionRequest({
      params,
      requestId,
    }: {
      params?: {
        pgs?: string;
        position?: number | null;
        recommendation?: boolean;
      };
      requestId: number;
    }) {
      const search = generateParams([
        ["pgs", params?.pgs],
        ["position", params?.position],
        ["recommendation", params?.recommendation ? "true" : undefined],
      ]);
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/requests/${requestId}`,
        search,
      });
    },
    goToAuctionRequestTab({
      requestId,
      tab,
    }: {
      requestId: number;
      tab: MessengerTabKinds;
    }) {
      const search = new URLSearchParams(location.search);
      search.set("activeTab", tab);
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/requests/${requestId}`,
        search: search.toString(),
      });
    },
    goToBarthel({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/requests/${auctionRequestId}/barthel-index`,
        search: undefined,
      });
    },
    // Notifications
    goToCareproviderNotificationPanel() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/notification-panel`,
        search: undefined,
      });
    },

    goToCareproviderReport(tab: AnalyticsSection) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/report/${tab}`,
        search: undefined,
      });
    },

    goToCareproviderSettings() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings`,
        search: undefined,
      });
    },

    goToCareproviderSettingsAccount() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/account`,
        search: undefined,
      });
    },

    goToCareproviderSettingsAdmissions() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/admissions`,
        search: undefined,
      });
    },

    goToCareproviderSettingsCapacity() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/capacity`,
        search: undefined,
      });
    },
    goToCareproviderSettingsCompanyProfile() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/profile`,
        search: undefined,
      });
    },
    goToCareproviderSettingsExtensions() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/extensions`,
        search: undefined,
      });
    },
    goToCareproviderSettingsLegalDocuments() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/legal-documents`,
        search: undefined,
      });
    },
    goToCareproviderSettingsNotifications() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/notifications`,
        search: undefined,
      });
    },
    goToCareproviderSettingsPartnerClinics() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/partner-clinics`,
        search: undefined,
      });
    },
    goToCareproviderSettingsPWA() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/pwa`,
        search: undefined,
      });
    },

    goToCareproviderSettingsSearch() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/search`,
        search: undefined,
      });
    },

    goToCareproviderSettingsServices() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/services`,
        search: undefined,
      });
    },
    goToCareproviderSettingsTeam() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/settings/team`,
        search: undefined,
      });
    },
    goToChangePassword() {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/my-password-change`,
        search: `${REDIRECT_PARAM}=${encodeURIComponent(
          `${location.pathname}${location.search}`,
        )}`,
      });
    },
    goToContactUs() {
      return "https://recaresolutions.com/kontakt/";
    },
    goToEarlyRehab({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/requests/${auctionRequestId}/early-rehab-index`,
        search: undefined,
      });
    },
    goToFilteredDashboard(filterId?: number) {
      const search = generateParams([["filterid", filterId]]);

      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/requests/new`,
        search,
      });
    },
    goToForgotPassword() {
      return pathToUrl({ pathname: "/careprovider/forgot-password", search });
    },
    goToLogin() {
      return pathToUrl({ pathname: "/careprovider/auth", search });
    },
    // Onboarding
    goToPersonalOnboarding({
      careproviderId,
      step = "account",
    }: {
      careproviderId: number;
      step?: string;
    }) {
      return pathToUrl({
        pathname: `/careprovider/newonboarding/personal/${careproviderId}/${step}`,
        search: location.search,
      });
    },
    goToProductionEnv() {
      return buildAppUrl({
        app: APP_CAREPROVIDER,
        env: ENV_PRODUCTION,
        path: getAppRoutePrefix(APP_CAREPROVIDER),
      });
    },
    // User account
    goToProfile(id: number) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${id}/my_account`,
        search: location.search,
      });
    },
    // Requests
    goToRequest(requestId: number, careproviderId: number) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/requests/${requestId}`,
        search: undefined,
      });
    },
    // Dashboard
    goToRequestDashboard(
      parameters: {
        pgs?: string;
        routeTab?: string;
        updatedRequestId?: number;
      } = {},
    ) {
      const { pgs, routeTab, updatedRequestId } = {
        routeTab: CAREPROVIDER_TAB_TYPE_NEW,
        ...parameters,
      };

      if (updatedRequestId) setUpdatedRequest(updatedRequestId);
      const search = generateParams([["pgs", pgs]]) || "";

      return pathToUrl({
        pathname: `/careprovider/dashboard/${loggedCareproviderId}/requests/${routeTab}`,
        search,
      });
    },
    goToRequestsLogged(careproviderId: number, updatedRequest?: number) {
      const search = updatedRequest ? `?updatedRequest=${updatedRequest}` : "";
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/requests`,
        search,
      });
    },
    goToSenderApp() {
      return buildAppUrl({
        app: APP_CLINIC,
        path: getAppRoutePrefix(APP_CLINIC),
      });
    },
    // Settings
    goToSettingsAccount(careproviderId: number) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/settings/accounts`,
        search: undefined,
      });
    },
    goToSinger({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      return pathToUrl({
        pathname: `/careprovider/dashboard/${careproviderId}/requests/${auctionRequestId}/singer-index`,
        search: undefined,
      });
    },
    // Authentication
    replaceByAuth() {
      return pathToUrl({ pathname: "/careprovider/auth", search: undefined });
    },
  } as const;
};

export const getCareproviderNavigationHandlers = ({
  careproviderId,
  location,
  navigate,
}: {
  careproviderId?: number;
  location: Location;
  navigate: NavigateFunction;
}) => {
  const urls = getCareproviderHandlerUrls({
    location,
    loggedCareproviderId: careproviderId,
  });
  return {
    changePassword(id: number) {
      navigate(urls.changePassword(id));
    },
    // Admissions form
    goToAdmissionsForm({
      admissionsId,
      step,
    }: {
      admissionsId: string;
      step: AdmissionsStep;
    }) {
      navigate(urls.goToAdmissionsForm({ admissionsId, step }));
    },
    goToAdmissionsFormPreview({
      formType,
      step,
    }: {
      formType: AdmissionsFormType;
      step: AdmissionsStep;
    }) {
      navigate(urls.goToAdmissionsFormPreview({ formType, step }));
    },
    goToAdmissionsInfoScreens({
      admissionsId,
      infoType,
    }: {
      admissionsId: string;
      infoType: AdmissionsInfoScreen;
    }) {
      navigate(urls.goToAdmissionsInfoScreens({ admissionsId, infoType }));
    },
    goToAdmissionsLogin({ admissionsId }: { admissionsId: string }) {
      navigate(urls.goToAdmissionsLogin({ admissionsId }));
    },

    goToAuctionRequest({
      params,
      requestId,
    }: {
      params?: {
        pgs?: string;
        position?: number | null;
        recommendation?: boolean;
      };
      requestId: number;
    }) {
      navigate(urls.goToAuctionRequest({ params, requestId }));
    },

    goToAuctionRequestTab({
      requestId,
      tab,
    }: {
      requestId: number;
      tab: MessengerTabKinds;
    }) {
      navigate(urls.goToAuctionRequestTab({ requestId, tab }));
    },

    goToBarthel({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      navigate(urls.goToBarthel({ auctionRequestId, careproviderId }));
    },

    // Notifications
    goToCareproviderNotificationPanel() {
      navigate(urls.goToCareproviderNotificationPanel());
    },

    goToCareproviderReport(tab: AnalyticsSection) {
      navigate(urls.goToCareproviderReport(tab));
    },

    goToCareproviderSettings() {
      navigate(urls.goToCareproviderSettings());
    },
    goToCareproviderSettingsAccount() {
      navigate(urls.goToCareproviderSettingsAccount());
    },
    goToCareproviderSettingsAdmissions() {
      navigate(urls.goToCareproviderSettingsAdmissions());
    },
    goToCareproviderSettingsCapacity() {
      navigate(urls.goToCareproviderSettingsCapacity());
    },
    goToCareproviderSettingsCompanyProfile() {
      navigate(urls.goToCareproviderSettingsCompanyProfile());
    },
    goToCareproviderSettingsExtensions() {
      navigate(urls.goToCareproviderSettingsExtensions());
    },
    goToCareproviderSettingsLegalDocuments() {
      navigate(urls.goToCareproviderSettingsLegalDocuments());
    },

    goToCareproviderSettingsNotifications() {
      navigate(urls.goToCareproviderSettingsNotifications());
    },

    goToCareproviderSettingsPartnerClinics() {
      navigate(urls.goToCareproviderSettingsPartnerClinics());
    },
    goToCareproviderSettingsPWA() {
      navigate(urls.goToCareproviderSettingsPWA());
    },
    goToCareproviderSettingsSearch() {
      navigate(urls.goToCareproviderSettingsSearch());
    },
    goToCareproviderSettingsServices() {
      navigate(urls.goToCareproviderSettingsServices());
    },
    goToCareproviderSettingsTeam() {
      navigate(urls.goToCareproviderSettingsTeam());
    },
    goToChangePassword() {
      navigate(urls.goToChangePassword());
    },
    goToEarlyRehab({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      navigate(urls.goToEarlyRehab({ auctionRequestId, careproviderId }));
    },
    goToFilteredDashboard(filterId?: number) {
      navigate(urls.goToFilteredDashboard(filterId));
    },
    goToForgotPassword() {
      navigate(urls.goToForgotPassword());
    },
    // Authentication
    goToLogin() {
      navigate(urls.goToLogin());
    },
    // Onboarding
    goToPersonalOnboarding({
      careproviderId,
      step = "account",
    }: {
      careproviderId: number;
      step?: string;
    }) {
      navigate(urls.goToPersonalOnboarding({ careproviderId, step }));
    },
    // User account
    goToProfile(id: number) {
      navigate(urls.goToProfile(id));
    },
    // Requests
    goToRequest(requestId: number, careproviderId: number) {
      navigate(urls.goToRequest(requestId, careproviderId));
    },
    // Dashboard
    goToRequestDashboard(
      parameters: {
        pgs?: string;
        routeTab?: string;
        updatedRequestId?: number;
      } = {},
    ) {
      const { pgs, routeTab, updatedRequestId } = {
        routeTab: CAREPROVIDER_TAB_TYPE_NEW,
        ...parameters,
      };

      if (updatedRequestId) setUpdatedRequest(updatedRequestId);

      navigate(urls.goToRequestDashboard({ pgs, routeTab, updatedRequestId }));
    },
    goToRequestsLogged(careproviderId: number, updatedRequest?: number) {
      navigate(urls.goToRequestsLogged(careproviderId, updatedRequest));
    },
    // Settings
    goToSettingsAccount(careproviderId: number) {
      navigate(urls.goToSettingsAccount(careproviderId));
    },
    goToSinger({
      auctionRequestId,
      careproviderId,
    }: {
      auctionRequestId: number;
      careproviderId: number;
    }) {
      navigate(urls.goToSinger({ auctionRequestId, careproviderId }));
    },
    replaceByAuth() {
      navigate(urls.replaceByAuth(), { replace: true });
    },
  };
};
